import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Avatar, Box, IconButton } from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getRealm } from "../../../../AuthProvider";
import ElevatedPaper, {
  DEFAULT_STYLE,
} from "../../../../components/ElevatedPaper";
import Flex from "../../../../components/Flex";
import MultiSpecialty from "../../../../components/MultiSpecialty";
import OutlineCard from "../../../../components/OutlineCard";
import Text from "../../../../components/Text";
import {
  GENDER_RECORD,
  STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT,
  TIME_ZONE_MAPPER,
} from "../../../../constants";
import { useProvider } from "../../../../queries/use-provider";
import { formatDate } from "../../../../utils/date-time-utility";
import { DEFAULT_USER_SRC } from "../../../../utils/utils";

const FONT = "14px";
const LABEL_WIDTH = "145px";

const PatientProviderProfile = () => {
  const { providerUuid } = useParams<{ providerUuid: string }>();
  const navigate = useNavigate();
  const [showAllInsurance, setShowAllInsurance] = useState(false);

  const hostNameArr = location.hostname?.split(".");
  const xTENANTID = getRealm(hostNameArr);

  const { data: providerRes } = useProvider({
    providerUuid: providerUuid ?? "",
    tenantId: xTENANTID ?? undefined,
  });

  const provider = providerRes?.data as any;

  const insurances: string[] = provider?.acceptedInsurances
    ? Object.values(provider.acceptedInsurances as Record<string, string>)
    : [];

  const visibleInsurances = showAllInsurance
    ? insurances
    : insurances.slice(0, 10);

  const workLocations: string[] = provider?.workLocations
    ? Object.values(provider.workLocations as Record<string, string>)
    : [];

  const profileRows = [
    { label: "Role", value: provider?.role },
    { label: "Provider Type", value: provider?.providerType },
    {
      label: "Gender",
      value: GENDER_RECORD?.[provider?.gender as string] || provider?.gender,
    },
    { label: "Contact Number", value: provider?.phone },
    { label: "Email", value: provider?.email },
    { label: "NPI Number", value: provider?.npi?.toString() },
    { label: "Bio", value: provider?.bio },
  ];

  const basicInfoRows = [
    { label: "Office Fax Number", value: provider?.officeFaxNumber },
    { label: "Group NPI Number", value: provider?.groupNpiNumber?.toString() },
    { label: "Taxonomy Number", value: provider?.taxonomyNumber },
    {
      label: "Time Zone",
      value:
        TIME_ZONE_MAPPER?.[provider?.timezone as string] || provider?.timezone,
    },
  ];

  return (
    <Box width="100%" height="100%" overflow="auto">
      <ElevatedPaper sx={{ ...DEFAULT_STYLE, height: "100%" }}>
        <Flex flexDirection="column" gap={2} width="100%" height="100%">
          {/* Header */}
          <Flex alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Text fontWeight="medium" fontSize="16px">
              Provider Profile
            </Text>
          </Flex>

          {/* Content */}
          <Flex gap={2} width="100%" flex={1} alignItems="flex-start" mt={1}>
            {/* Left Card */}
            <OutlineCard
              sx={{ width: "442px", minWidth: "300px", minHeight: "550px" }}
            >
              <Flex flexDirection="column" gap={1.5} p={"12px"}>
                <Avatar
                  src={provider?.avatar ?? DEFAULT_USER_SRC}
                  alt={provider?.firstName}
                  sx={{ width: 90, height: 90 }}
                />
                <Text fontWeight="medium" fontSize="16px">
                  {[provider?.firstName, provider?.lastName]
                    .filter(Boolean)
                    .join(" ") || "-"}
                </Text>
                <MultiSpecialty
                  specialties={provider?.specialities}
                  width={280}
                />

                <Box width="100%" mt={0.5}>
                  {profileRows.map(({ label, value }) =>
                    value ? (
                      <Flex
                        key={label}
                        mb={1.5}
                        gap={0.5}
                        alignItems="flex-start"
                      >
                        <Text
                          fontSize={FONT}
                          color="text.secondary"
                          sx={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}
                        >
                          {label}
                        </Text>
                        <Text fontSize={FONT} sx={{ wordBreak: "break-word" }}>
                          : {value}
                        </Text>
                      </Flex>
                    ) : null
                  )}
                </Box>
              </Flex>
            </OutlineCard>

            {/* Right Card */}
            <OutlineCard sx={{ flex: 1, padding: "20px", height: "362px" }}>
              <Text fontWeight="medium" fontSize="16px" mb={2.5}>
                Basic Information
              </Text>
              <Flex gap={3} alignItems="flex-start">
                {/* Left column */}
                <Box flex={1}>
                  {basicInfoRows.map(({ label, value }) =>
                    value ? (
                      <Flex
                        key={label}
                        mb={2}
                        gap={0.5}
                        alignItems="flex-start"
                      >
                        <Text
                          fontSize={FONT}
                          color="text.secondary"
                          sx={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}
                        >
                          {label}
                        </Text>
                        <Text fontSize={FONT}>: {value}</Text>
                      </Flex>
                    ) : null
                  )}

                  {/* Licensed Detail */}
                  {provider?.licenceInformation?.length > 0 && (
                    <Flex mb={2} gap={0.5} alignItems="flex-start">
                      <Text
                        fontSize={FONT}
                        color="text.secondary"
                        sx={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}
                      >
                        Licensed Detail
                      </Text>
                      <Flex flexDirection="column" gap={0.5}>
                        {provider.licenceInformation.map(
                          (lic: any, i: number) => (
                            <Text key={i} fontSize={FONT}>
                              :{" "}
                              {`${i + 1}. ${
                                STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT?.[
                                  lic.licenseState
                                ] ??
                                lic.licenseState ??
                                ""
                              }${
                                lic.licenseNumber
                                  ? ` (${lic.licenseNumber})`
                                  : ""
                              }${
                                lic.expiryDate
                                  ? ` (${formatDate(lic.expiryDate)})`
                                  : ""
                              }`}
                            </Text>
                          )
                        )}
                      </Flex>
                    </Flex>
                  )}

                  {/* Work Location */}
                  {workLocations.length > 0 && (
                    <Flex mb={2} gap={0.5} alignItems="flex-start">
                      <Text
                        fontSize={FONT}
                        color="text.secondary"
                        sx={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}
                      >
                        Work Location
                      </Text>
                      <Flex flexDirection="column" gap={0.5}>
                        {workLocations.map((loc, i) => (
                          <Text key={i} fontSize={FONT}>
                            : {i + 1}. {loc}
                          </Text>
                        ))}
                      </Flex>
                    </Flex>
                  )}
                </Box>

                {/* Right column – Insurance */}
                {insurances.length > 0 && (
                  <Box flex={1}>
                    <Flex gap={0.5} alignItems="flex-start">
                      <Text
                        fontSize={FONT}
                        color="text.secondary"
                        sx={{ minWidth: LABEL_WIDTH, flexShrink: 0 }}
                      >
                        Insurance(s) Accepted
                      </Text>
                      <Flex flexDirection="column" gap={0.5}>
                        {visibleInsurances.map((ins, i) => (
                          <Text key={i} fontSize={FONT}>
                            {i + 1}. {ins}
                          </Text>
                        ))}
                        {insurances.length > 10 && (
                          <Flex
                            alignItems="center"
                            gap={0.25}
                            sx={{ cursor: "pointer", mt: 0.5 }}
                            onClick={() => setShowAllInsurance((prev) => !prev)}
                          >
                            <Text fontSize={FONT} color="primary.main">
                              {showAllInsurance ? "Show Less" : "Show All"}
                            </Text>
                            {showAllInsurance ? (
                              <ExpandLessIcon
                                sx={{ fontSize: 16, color: "primary.main" }}
                              />
                            ) : (
                              <ExpandMoreIcon
                                sx={{ fontSize: 16, color: "primary.main" }}
                              />
                            )}
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                )}
              </Flex>
            </OutlineCard>
          </Flex>
        </Flex>
      </ElevatedPaper>
    </Box>
  );
};

export default PatientProviderProfile;
